/**
 * Microsoft Clarity loader + Consent Mode v2 bridge + safe event mirror.
 *
 * Project: ya2b98n3bu (public identifier)
 *
 * Design decisions
 * ----------------
 * * ONE loader. `bootClarity()` is idempotent — subsequent calls no-op after
 *   the first script tag is inserted. There is no `<script>` tag for Clarity
 *   in `public/index.html`; the boot snippet is emitted from JS so the
 *   loader lives in a single, testable, version-controlled place.
 * * DENIED BY DEFAULT. `bootClarity()` primes the shim on `window.clarity`
 *   and immediately calls `clarity('consent', 'v2', { ad_Storage:'denied',
 *   analytics_Storage:'denied' })` before the CDN JS resolves. The exact,
 *   case-sensitive keys are required by Microsoft's Consent API v2.
 * * NEVER any PII. The mirror surface is limited to:
 *     - Event NAMES only (no parameters) via `clarity('event', name)`.
 *     - A short static tag set via `clarity('set', key, value)` that
 *       carries only page_type / checkout_stage — never product ids,
 *       cart contents, order ids, session ids, prices, addresses,
 *       search text, email, name, or any other user data.
 *   The allow-list is enforced in code below.
 * * NEVER a customer identifier. `clarity('identify', ...)` is intentionally
 *   not exposed. If a future refactor tries to reach for it, the linter
 *   will flag the unknown export and code review will catch it.
 */

// Microsoft Clarity project id (public — safe to ship in the frontend bundle).
export const CLARITY_PROJECT_ID = "ya2b98n3bu";

// Storage key of the cookie-consent record produced by CookieConsent.jsx.
// Kept in sync with that component; we intentionally do not import from it
// to avoid a React dependency in a plain JS module.
export const CONSENT_STORAGE_KEY = "mps_cookie_consent_v1";

// Event names that are safe to mirror to Clarity. Anything not in this
// set is silently dropped even if a future refactor tries to send it.
const ALLOWED_EVENT_NAMES = new Set([
  "view_item",
  "add_to_cart",
  "view_cart",
  "remove_from_cart",
  "begin_checkout",
  "add_shipping_info",
  "add_payment_info",
  "purchase",
  "search",
  "checkout_error",
  "payment_failed",
]);

// Tag keys that are safe to attach to a Clarity session. Values MUST be
// short, low-cardinality, contain no PII/product/order/customer data.
const ALLOWED_TAG_KEYS = new Set([
  "page_type",       // e.g. "home" | "pdp" | "cart" | "checkout" | "payment_success"
  "checkout_stage",  // e.g. "shipping" | "payment_redirect" | "confirming" | "paid"
]);

const CLARITY_SCRIPT_ID = "mps-clarity-loader";

/**
 * Return a stable shim that mirrors Microsoft's public API. Available
 * immediately even before the CDN script resolves — calls queue on the
 * internal buffer and are drained once the real function replaces the
 * shim. Kept module-private so nothing outside this file can reach `q`.
 */
function ensureClarityShim() {
  if (typeof window === "undefined") return () => {};
  if (typeof window.clarity === "function") return window.clarity;
  const c = (...args) => { c.q = c.q || []; c.q.push(args); };
  c.q = [];
  window.clarity = c;
  return c;
}

/**
 * Boot the Microsoft Clarity CDN loader exactly once.
 *
 * Idempotent — the second and later calls short-circuit on the DOM
 * script tag id. Denied-by-default consent is signalled BEFORE the
 * script tag is appended, so no cookie can ever be set until the
 * shopper's decision has been mirrored via `applyClarityConsent`.
 */
export function bootClarity({ projectId = CLARITY_PROJECT_ID, document: doc, window: win } = {}) {
  const d = doc || (typeof document !== "undefined" ? document : null);
  const w = win || (typeof window !== "undefined" ? window : null);
  if (!d || !w) return { booted: false, reason: "no_window" };
  if (d.getElementById(CLARITY_SCRIPT_ID)) return { booted: false, reason: "already_booted" };

  const c = ensureClarityShim();
  // MUST be called BEFORE the CDN script is appended so the initial
  // handshake carries denied signals. The Microsoft API is case-sensitive
  // on `ad_Storage` and `analytics_Storage` — do not "correct" the casing.
  c("consent", "v2", { ad_Storage: "denied", analytics_Storage: "denied" });

  const s = d.createElement("script");
  s.async = true;
  s.id = CLARITY_SCRIPT_ID;
  s.src = "https://www.clarity.ms/tag/" + encodeURIComponent(projectId);
  const first = d.getElementsByTagName("script")[0];
  if (first && first.parentNode) first.parentNode.insertBefore(s, first);
  else d.head.appendChild(s);
  return { booted: true, reason: "ok" };
}

/**
 * Read the persisted consent record and translate it into a Microsoft
 * Consent v2 signal. NEVER guesses — if the record is absent, both
 * signals stay denied.
 *
 * Contract for the caller (CookieConsent.jsx / AnalyticsLoader.jsx):
 *   - "Accept analytics"  → { analytics: true,  marketing: false }
 *   - "Essential only"    → { analytics: false, marketing: false }
 * Since our banner offers no marketing-consent choice, ad_Storage is
 * pinned to `denied` regardless of the analytics decision.
 */
export function applyClarityConsent(record) {
  const c = ensureClarityShim();
  const r = record && typeof record === "object" ? record : null;
  const analytics = !!(r && r.analytics);
  const marketing = !!(r && r.marketing);
  c("consent", "v2", {
    ad_Storage:        marketing ? "granted" : "denied",
    analytics_Storage: analytics ? "granted" : "denied",
  });
  return {
    ad_Storage:        marketing ? "granted" : "denied",
    analytics_Storage: analytics ? "granted" : "denied",
  };
}

/**
 * Read the CookieConsent-produced record from localStorage. Returns
 * `null` if absent or unparseable — the caller MUST treat that as
 * "denied by default" (which `applyClarityConsent(null)` does).
 */
export function readConsentRecord({ storage } = {}) {
  const s = storage || (typeof localStorage !== "undefined" ? localStorage : null);
  if (!s) return null;
  try {
    const raw = s.getItem(CONSENT_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

/**
 * Mirror an event NAME to Clarity. Parameters are DELIBERATELY ignored
 * — Microsoft's `clarity('event', name)` API takes a string only, and
 * we do not want product/cart/session/customer data reaching the
 * heatmap tool. Unknown names are dropped silently.
 */
export function clarityTrack(eventName) {
  if (typeof eventName !== "string") return false;
  if (!ALLOWED_EVENT_NAMES.has(eventName)) return false;
  const c = ensureClarityShim();
  c("event", eventName);
  return true;
}

/**
 * Attach a low-cardinality tag to the Clarity session. Only the keys in
 * ALLOWED_TAG_KEYS are honoured; unknown keys are dropped. Values are
 * capped at 40 chars, coerced to strings, and rejected if they contain
 * an "@" (email safeguard) or a digit run of >6 (id safeguard).
 */
export function claritySet(key, value) {
  if (!ALLOWED_TAG_KEYS.has(key)) return false;
  const raw = value == null ? "" : String(value);
  if (raw.includes("@")) return false;
  if (/\d{7,}/.test(raw)) return false;
  const clean = raw.slice(0, 40);
  const c = ensureClarityShim();
  c("set", key, clean);
  return true;
}

// Test helper — reset the module-level guards so unit tests can boot
// again after simulating a fresh document. Not part of the public API;
// consumers should not import this in production code.
export function __resetClarityForTests() {
  if (typeof document === "undefined") return;
  const s = document.getElementById(CLARITY_SCRIPT_ID);
  if (s && s.parentNode) s.parentNode.removeChild(s);
  if (typeof window !== "undefined") delete window.clarity;
}
